import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronRight } from 'lucide-react';
import nhost from '../nhost';

const GEO_URL = 'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson';

const NAME_TO_SWEDISH = {
  'France': 'Frankrike',
};

const ISO_TO_SWEDISH = {
  FRA: 'Frankrike', ITA: 'Italien', ESP: 'Spanien', PRT: 'Portugal',
  DEU: 'Tyskland', AUT: 'Österrike', CHE: 'Schweiz', HUN: 'Ungern',
  GRC: 'Grekland', SVN: 'Slovenien', HRV: 'Kroatien', ROU: 'Rumänien',
  BGR: 'Bulgarien', GEO: 'Georgien', MDA: 'Moldavien', ARM: 'Armenien',
  USA: 'USA', CAN: 'Kanada', ARG: 'Argentina', CHL: 'Chile',
  URY: 'Uruguay', BRA: 'Brasilien', AUS: 'Australien', NZL: 'Nya Zeeland',
  ZAF: 'Sydafrika', MAR: 'Marocko', ISR: 'Israel', LBN: 'Libanon',
  CHN: 'Kina', JPN: 'Japan', TUR: 'Turkiet', CYP: 'Cypern',
  LUX: 'Luxemburg', MKD: 'Makedonien', ALB: 'Albanien', SRB: 'Serbien',
  MNE: 'Montenegro', BIH: 'Bosnien och Hercegovina', GBR: 'Storbritannien',
  NOR: 'Norge', SWE: 'Sverige',
};

// Delländer i Storbritannien ritas som ett enda land på kartan.
const COUNTRY_ALIASES = {
  'England': 'Storbritannien', 'Skottland': 'Storbritannien',
  'Wales': 'Storbritannien', 'Nordirland': 'Storbritannien',
};

function wineCountry(w) {
  const c = w.country?.trim() || extractCountry(w.region);
  return COUNTRY_ALIASES[c] ?? c;
}

function extractCountry(region) {
  if (!region) return null;
  const parts = region.split(',').map(s => s.trim());
  return parts[parts.length - 1];
}

function interpolateColor(t) {
  const stops = [[245,232,235],[196,86,106],[92,26,40]];
  const i = t <= 0.5 ? 0 : 1;
  const local = t <= 0.5 ? t * 2 : (t - 0.5) * 2;
  const [r1,g1,b1] = stops[i];
  const [r2,g2,b2] = stops[i+1];
  return `rgb(${Math.round(r1+(r2-r1)*local)},${Math.round(g1+(g2-g1)*local)},${Math.round(b1+(b2-b1)*local)})`;
}

function getColor(count, maxCount) {
  if (!count || !maxCount) return '#ede4e7';
  const t = Math.log(count + 1) / Math.log(maxCount + 1);
  return interpolateColor(Math.min(t, 1));
}

function CountryZoomer({ feature, onDone }) {
  const map = useMap();
  useEffect(() => {
    if (!feature) return;
    const bounds = L.geoJSON(feature).getBounds();
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 6 });
    onDone();
  }, [feature]); // eslint-disable-line
  return null;
}

export default function WorldMap({ onNavigate, onSetSearch, onCountrySelect }) {
  const [wines, setWines] = useState([]);
  const [winesLoaded, setWinesLoaded] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [zoomFeature, setZoomFeature] = useState(null);

  useEffect(() => {
    nhost.graphql.request(`query { wines { id country region event_id
      session { completed wines_visible }
      event { event_type published }
    } }`)
      .then(({ data }) => {
        if (data?.wines) setWines(data.wines.filter(w =>
          (!w.session && !w.event_id) ||
          (w.session && (w.session.completed || w.session.wines_visible)) ||
          (w.event_id && w.event?.event_type === 'wine_box' && w.event?.published)
        ));
        setWinesLoaded(true);
      })
      .catch(() => setWinesLoaded(true));
    fetch(GEO_URL).then(r => r.json()).then(setGeoData);
  }, []);

  const winesByCountry = useMemo(() => {
    const map = {};
    wines.forEach(w => {
      const c = wineCountry(w);
      if (c) map[c] = (map[c] || 0) + 1;
    });
    return map;
  }, [wines]);

  const maxCountry = useMemo(
    () => Math.max(...Object.values(winesByCountry), 1),
    [winesByCountry]
  );

  const sortedCountries = useMemo(() =>
    Object.entries(winesByCountry).sort((a, b) => b[1] - a[1]),
    [winesByCountry]
  );

  function openCountry(name, feature) {
    const countryWines = wines.filter(w => wineCountry(w) === name);
    const regionMap = {};
    countryWines.forEach(w => {
      const r = w.region || 'Okänd region';
      regionMap[r] = (regionMap[r] || 0) + 1;
    });
    const regions = Object.entries(regionMap).sort((a, b) => b[1] - a[1]);
    const wineIds = countryWines.map(w => w.id);
    onCountrySelect({ name, regions, total: countryWines.length, wineIds });
    if (feature) setZoomFeature(feature);
  }

  const winesByCountryRef = useRef({});
  winesByCountryRef.current = winesByCountry;
  const maxCountryRef = useRef(1);
  maxCountryRef.current = maxCountry;

  const countryStyle = useCallback((feature) => {
    const iso = feature.properties['ISO3166-1-Alpha-3'];
    const countryName = ISO_TO_SWEDISH[iso] ?? NAME_TO_SWEDISH[feature.properties['name']];
    const count = countryName ? (winesByCountryRef.current[countryName] || 0) : 0;
    return {
      fillColor: getColor(count, maxCountryRef.current),
      fillOpacity: count > 0 ? 0.85 : 0.4,
      color: 'rgba(139,42,62,0.25)',
      weight: 0.8,
    };
  }, []);

  const onEachFeature = useMemo(() => (feature, lyr) => {
    const iso = feature.properties['ISO3166-1-Alpha-3'];
    const countryName = ISO_TO_SWEDISH[iso] ?? NAME_TO_SWEDISH[feature.properties['name']];
    const count = countryName ? (winesByCountry[countryName] || 0) : 0;
    if (count > 0) {
      lyr.on({
        click: () => openCountry(countryName, feature),
        mouseover: e => {
          setTooltip(`${countryName} – ${count} vin${count !== 1 ? 'er' : ''}`);
          e.target.setStyle({ fillOpacity: 0.6 });
          const el = e.target.getElement?.();
          if (el) el.style.cursor = 'pointer';
        },
        mouseout: e => {
          setTooltip(null);
          e.target.setStyle({ fillOpacity: 0.85 });
        },
      });
    }
  }, [winesByCountry]); // eslint-disable-line

  const ready = geoData && winesLoaded;

  return (
    <div style={{
      position: 'fixed', top: 60, left: 0, right: 0, bottom: 64,
      display: 'flex', flexDirection: 'column',
      background: 'var(--cream)',
      overflowY: 'auto',
    }}>
      {/* Karta */}
      <div style={{ position: 'relative', height: 260, flexShrink: 0 }}>
        {ready ? (
          <MapContainer
            center={[20, 10]}
            zoom={2}
            minZoom={1}
            maxZoom={8}
            style={{ width: '100%', height: '100%' }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" />
            <GeoJSON
              key={`${wines.length}-${maxCountry}-${Object.keys(winesByCountry).length}`}
              data={geoData}
              style={countryStyle}
              onEachFeature={onEachFeature}
            />
            {zoomFeature && (
              <CountryZoomer feature={zoomFeature} onDone={() => setZoomFeature(null)} />
            )}
          </MapContainer>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14 }}>
            Laddar karta…
          </div>
        )}

        {/* Gradient legend */}
        {maxCountry > 1 && (
          <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(255,255,255,.9)', border: '1px solid rgba(60,20,30,.08)', borderRadius: 10, padding: '5px 10px', zIndex: 1000, pointerEvents: 'none' }}>
            <div style={{ width: 100 }}>
              <div style={{ height: 5, borderRadius: 3, background: 'linear-gradient(to right, #F5E8EB, #C4566A, #5C1A28)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 9, color: 'var(--muted)' }}>1</span>
                <span style={{ fontSize: 9, color: 'var(--muted)' }}>{maxCountry}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tooltip */}
        {tooltip && (
          <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: 'var(--wine)', color: 'white', padding: '6px 14px', borderRadius: 20, fontSize: 13, pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 20 }}>
            {tooltip}
          </div>
        )}
      </div>

      {/* Hjälptext */}
      <div style={{ padding: '8px 16px 4px', textAlign: 'center', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          {sortedCountries.length} länder · {wines.length} viner · Tryck på ett land för detaljer
        </div>
      </div>

      {/* Länderlista */}
      {sortedCountries.length > 0 && (
        <div style={{ padding: '8px 16px 16px' }}>
          <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 16, overflow: 'hidden' }}>
            {sortedCountries.map(([country, count], i) => {
              const share = count / wines.length;
              return (
                <div
                  key={country}
                  onClick={() => openCountry(country, null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 14px',
                    borderTop: i > 0 ? '1px solid rgba(60,20,30,.07)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{country}</span>
                      <span style={{ fontSize: 12, color: 'var(--muted)', flexShrink: 0, marginLeft: 8 }}>{count} vin{count !== 1 ? 'er' : ''}</span>
                    </div>
                    <div style={{ height: 5, background: 'rgba(60,20,30,.07)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.round(share * 100)}%`, background: getColor(count, maxCountry), borderRadius: 3, minWidth: 4 }} />
                    </div>
                  </div>
                  <ChevronRight size={16} color="var(--muted)" strokeWidth={1.7} style={{ flexShrink: 0 }} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {winesLoaded && sortedCountries.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', fontSize: 14 }}>
          Inga viner med land registrerade ännu.
        </div>
      )}
    </div>
  );
}
